import styled from '@emotion/styled';
import { t } from '@lingui/macro';
import { useTableContext } from '../providers/TableProvider';
import { CaretDoubleLeft, CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { TableVariant } from '../types';

interface PaginationProps {
  onPageChange?: () => void;
  showNavButtons?: boolean;
}

export const Pagination = ({ onPageChange, showNavButtons }: PaginationProps) => {
  const { table, variant = 'default' } = useTableContext();
  const { pageIndex } = table.getState().pagination;

  return (
    <Container $variant={variant}>
      {/* Navigation buttons - only shown when pagination is needed */}
      {showNavButtons && (
        <NavButtons>
          <NavButton
            onClick={() => {
              onPageChange?.();
              table.setPageIndex(0);
            }}
            disabled={!table.getCanPreviousPage()}
            title={t`Go to first page`}
            data-testid="pagination-first"
          >
            <CaretDoubleLeft size={16} weight="bold" />
          </NavButton>
          <NavButton
            onClick={() => {
              onPageChange?.();
              table.previousPage();
            }}
            disabled={!table.getCanPreviousPage()}
            title={t`Go to previous page`}
            data-testid="pagination-prev"
          >
            <CaretLeft size={16} weight="bold" />
          </NavButton>
          <NavButton
            onClick={() => {
              onPageChange?.();
              table.nextPage();
            }}
            disabled={!table.getCanNextPage()}
            title={t`Go to next page`}
            data-testid="pagination-next"
          >
            <CaretRight size={16} weight="bold" />
          </NavButton>

          <PageInfo data-testid="pagenumber">
            <div>Page</div>
            <strong>{pageIndex + 1}</strong>
          </PageInfo>
        </NavButtons>
      )}
    </Container>
  );
};

export const Container = styled.div<{ $variant?: TableVariant }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  height: 50px;
  padding: 0 16px;
  gap: 16px;
  background-color: ${p =>
    p.$variant === 'transparent' ? 'transparent' : p.theme.palette.bg.primary};
`;

const NavButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavButton = styled.button`
  padding: 0.5rem 1rem;
  background: none;
  border: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const PageInfo = styled.span`
  align-self: center;
  padding: 0.5rem 1rem;
  flex-shrink: 0;
  display: flex;
  gap: 0.25rem;
`;
